import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { Accordion, Button, DialogModal, Flex, Form, InputSwitch, Spacer, TextLink, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useResolve } from '@visiba/instantiation';
import { delay } from '@visiba/std';
import { ConsentService, UserConsent } from './consent.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';

interface ConsentDialogProps {
	trigger: React.ReactNode;
}

export function ConsentDialog(props: ConsentDialogProps) {
	return (
		<DialogModal.Root trigger={props.trigger}>
			<ConsentContent />
		</DialogModal.Root>
	);
}

type CellulaFormData = {
	analytics: boolean;
	functional: boolean;
	essential: boolean;
};

function ConsentContent() {
	const universalConfig = useResolve(UniversalConfig);
	const consentService = useResolve(ConsentService);
	const analyticService = useResolve(AnalyticService);

	function handleAccept() {
		consentService.acceptConsent(UserConsent.Analytic, UserConsent.Functional);
		analyticService.trackEvent(AnalyticCategories.CookieConsent, 'In dialog, user clicked accept additional cookies');
	}

	function handleDeny() {
		consentService.acceptConsent();
		analyticService.trackEvent(AnalyticCategories.CookieConsent, 'In dialog, user denied additional cookies');
	}

	async function handleSubmit(data: CellulaFormData) {
		const consents: UserConsent[] = [];
		if (data.analytics) consents.push(UserConsent.Analytic);
		if (data.functional) consents.push(UserConsent.Functional);

		consentService.acceptConsent(...consents);

		if (consents.length > 0) {
			analyticService.trackEvent(AnalyticCategories.CookieConsent, 'In dialog, user saved cookies with changes');
		} else {
			analyticService.trackEvent(AnalyticCategories.CookieConsent, 'In dialog, user saved cookies without changes');
		}

		await delay(250);
	}

	return (
		<Form.Root
			onSubmit={(d) => handleSubmit(d as CellulaFormData)}
			options={{
				defaultValues: {
					analytics: consentService.hasConsent(UserConsent.Analytic),
					functional: consentService.hasConsent(UserConsent.Functional),
					essential: consentService.hasConsent(UserConsent.Essential),
				},
			}}
		>
			<DialogModal.Title>
				<Text.h2 variant='headingSmall'>{withTranslation('Component.Consent.Dialog.Title')}</Text.h2>
			</DialogModal.Title>
			<DialogModal.Content>
				<Text variant='bodyRegular'>
					{withTranslation('Component.Consent.Dialog.ReadMore')}{' '}
					<TextLink
						size='small'
						href={universalConfig.alias + '/cookie-policy'}
						target='_blank'
						icon='linkOut'
						iconPosition='trailing'
					>
						{withTranslation('Component.Footer.CookiePolicy')}
					</TextLink>
				</Text>
				<Flex gap={tokens.size.x0_5}>
					<Text.p variant='bodyRegular'>{withTranslation('Component.Consent.Dialog.Body')}</Text.p>
					<Accordion.Root type='multiple'>
						<Accordion.Item
							header={
								<>
									<span>{withTranslation('Component.Consent.Dialog.Item.Analytic.Title')}</span>
									<InputSwitch name='analytics' onClick={(e) => e.stopPropagation()} />
								</>
							}
							value='analytics'
						>
							{withTranslation('Component.Consent.Dialog.Item.Analytic.Body')}
						</Accordion.Item>
						<Accordion.Item
							header={
								<>
									<span>{withTranslation('Component.Consent.Dialog.Item.Functional.Title')}</span>
									<InputSwitch name='functional' onClick={(e) => e.stopPropagation()} />
								</>
							}
							value='functional'
						>
							{withTranslation('Component.Consent.Dialog.Item.Functional.Body')}
						</Accordion.Item>
						<Accordion.Item
							header={
								<>
									<span>{withTranslation('Component.Consent.Dialog.Item.Essential.Title')}</span>
									<Text variant='labelSmallSemiBold' color={tokens.color.content.muted}>
										{withTranslation('Component.Consent.Dialog.Item.Essential.IsMandatory')}
									</Text>
								</>
							}
							value='essential'
						>
							{withTranslation('Component.Consent.Dialog.Item.Essential.Body')}
						</Accordion.Item>
					</Accordion.Root>
				</Flex>

				<Spacer vertical={tokens.spacing.x4} />
			</DialogModal.Content>
			<DialogModal.Footer>
				<Flex direction='row' gap={tokens.size.x1} justify='space-between'>
					<DialogModal.Close asChild onClick={handleDeny}>
						<Button size='xSmall' variant='secondary'>
							{withTranslation('Component.Consent.CTA.Deny')}
						</Button>
					</DialogModal.Close>
					<Flex direction='row' gap={tokens.size.x1}>
						<DialogModal.Close asChild onClick={handleAccept}>
							<Button size='xSmall' variant='secondary'>
								{withTranslation('Component.Consent.CTA.AcceptAll')}
							</Button>
						</DialogModal.Close>

						<Form.SubmitButton size='xSmall' variant='primary'>
							{withTranslation('Component.Consent.CTA.Save')}
						</Form.SubmitButton>
					</Flex>
				</Flex>
			</DialogModal.Footer>
		</Form.Root>
	);
}
